import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsPerson, BsKey, BsCheckLg } from 'react-icons/bs';
import { useAuth } from '../../contexts/AuthContext';
import { authenticatedFetch, getToken } from '../../utils/api';
import { setPreloadedData } from '../../utils/preloadStore';
import opusLogo from '../../images/manager-report/opus-logo.svg';
import copiaLogo from '../../images/manager-report/Copia247.png';
import './loginPage.scss';

const LOAD_STEPS = [
    'Authenticating credentials',
    'Validating session token',
    'Establishing secure connection',
    'Connecting to data source',
    'Checking access permissions',
    'Loading user profile',
    'Synchronising account settings',
    'Preparing workspace',
    'Retrieving assigned sites',
    'Loading dashboard configuration',
    'Fetching latest activity data',
    'Preparing analytics engine',
    'Checking compliance datasets',
    'Connecting to operational database',
    'Loading occurrence records',
    'Preparing notification services',
    'Loading audit trail records',
    'Retrieving recent operational data',
    'Loading dashboard widgets',
    'Fetching latest deployment data',
    'Retrieving shift deployments',
    'Checking shift fulfilment',
    'Loading site patrol records',
    'Retrieving NFC tagging activity',
    'Preparing patrol verification data',
    'Checking attendance compliance',
    'Loading time and attendance logs',
    'Preparing occurrence dashboard',
    'Checking site task completion',
    'Loading patrol route data',
    'Preparing incident analytics',
    'Retrieving staff licensing records',
    'Checking SIA licence status',
    'Preparing deployment overview',
    'Checking tagging integrity',
    'Preparing workforce analytics',
    'Loading site monitoring data',
    'Retrieving live operations summary',
    'Finalising session setup',
    'Portal access approved.',
];

const VISIBLE_COUNT = 4;
const STEP_MIN_MS = 250;
const STEP_MAX_MS = 1500;

const randomDelay = () => STEP_MIN_MS + Math.random() * (STEP_MAX_MS - STEP_MIN_MS);

function getDeviceInfo() {
    const ua = navigator.userAgent;
    const match = (patterns) => {
        for (const p of patterns) {
            if (new RegExp(p.value, 'i').test(ua)) {
                const vm = ua.match(new RegExp(p.version + '[- /:;]([\\d._]+)', 'i'));
                const ver = vm?.[1]?.split(/[._]+/) || ['0'];
                return { name: p.name, version: parseFloat(ver[0] + '.' + ver.slice(1).join('')) };
            }
        }
        return { name: 'unknown', version: 0 };
    };
    const os = match([
        { name: 'Windows', value: 'Win', version: 'NT' },
        { name: 'iPhone', value: 'iPhone', version: 'OS' },
        { name: 'iPad', value: 'iPad', version: 'OS' },
        { name: 'Android', value: 'Android', version: 'Android' },
        { name: 'Macintosh', value: 'Mac', version: 'OS X' },
        { name: 'Linux', value: 'Linux', version: 'rv' },
    ]);
    const browser = match([
        { name: 'Chrome', value: 'Chrome', version: 'Chrome' },
        { name: 'Firefox', value: 'Firefox', version: 'Firefox' },
        { name: 'Safari', value: 'Safari', version: 'Version' },
    ]);
    const w = window.screen.width * window.devicePixelRatio;
    const h = window.screen.height * window.devicePixelRatio;
    return `os.name = ${os.name}, os.version = ${os.version}, browser.name = ${browser.name}, browser.version = ${browser.version}, dimensions: ${w} x ${h}`;
}

function parseFlags(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
            copiaBase: payload.mr_copia_base != null,
            copiaDirectRostering: payload.mr_copia_direct_rostering != null,
            copiaQueryApprove: payload.mr_copia_query_approve != null,
            copiaTagging: payload.mr_copia_tagging != null,
            copiaOccurrenceBasic: payload.mr_copia_occurrence_basic != null,
            copiaOccurrenceAdvanced: payload.mr_copia_occurrence_advanced != null,
        };
    } catch {
        return {};
    }
}

async function fetchJson(url, opts = {}) {
    try {
        const res = await authenticatedFetch(url, opts);
        if (!res.ok) return null;
        return res.json();
    } catch {
        return null;
    }
}

// Parallel data preloading based on user access flags
async function preloadAllData(flags) {
    const deviceInfo = getDeviceInfo();
    const fetches = {};

    // Costings — always fetched (base access)
    fetches.report = fetchJson('/ManagerReport/GetMangerReport', {
        method: 'POST',
        body: JSON.stringify({ Deviceinfo: deviceInfo, Summary: 0 }),
    });
    fetches.settings = fetchJson('/ManagerReport/GetsettingsTableParams', { method: 'POST' });
    fetches.costingSummary = fetchJson('/ManagerReport/GetCostingSummaryData', { method: 'POST' });

    // Queries
    if (flags.copiaQueryApprove) {
        fetches.queries = fetchJson('/ManagerReport/GetSubmittedQueries', { method: 'POST' });
        fetches.resolutionTypes = fetchJson('/ManagerReport/GetResolutionTypes');
    }

    // Security / Fulfillment
    if (flags.copiaDirectRostering) {
        fetches.sites = fetchJson('/ManagerReport/GetSites');
        fetches.roles = fetchJson('/ManagerReport/GetRolesForRoster');
        fetches.financialWeeks = fetchJson('/ManagerReport/GetFinancialWeeks');
        fetches.fulfillment = fetchJson('/ManagerReport/GetSecurityFulfillmentSummary');
    }

    // Tagging/Patrol
    if (flags.copiaTagging) {
        fetches.tagging = fetchJson('/ManagerReport/GetTaggingData');
    }

    // Occurrences
    if (flags.copiaOccurrenceBasic) {
        fetches.occurrences = fetchJson('/ManagerReport/GetOccurrenceData');
    }

    // Documents — always fetched (no feature flag)
    fetches.documents = fetchJson('/ManagerReport/GetDocumentData');

    const keys = Object.keys(fetches);
    const results = await Promise.all(Object.values(fetches));
    const data = {};
    keys.forEach((k, i) => { data[k] = results[i]; });
    return data;
}

const LoginPage = () => {
    const { authenticateByLogin, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const [emailAddress, setEmailAddress] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [stepIndex, setStepIndex] = useState(-1);

    const dataReady = useRef(false);
    const preloadedRef = useRef(null);
    const timerRef = useRef(null);
    const loggingIn = useRef(false);

    const runSteps = useCallback(() => {
        let i = 0;
        setStepIndex(0);

        const scheduleNext = () => {
            timerRef.current = setTimeout(() => {
                i++;
                if (i < LOAD_STEPS.length - 1 && dataReady.current === false) {
                    setStepIndex(i);
                    scheduleNext();
                } else if (dataReady.current) {
                    setStepIndex(LOAD_STEPS.length - 1);
                    setTimeout(() => {
                        setPreloadedData(preloadedRef.current);
                        navigate('/report');
                    }, 750);
                } else {
                    scheduleNext();
                }
            }, randomDelay());
        };

        scheduleNext();
    }, [navigate]);

    const loading = stepIndex >= 0;

    useEffect(() => {
        return () => clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        if (isAuthenticated && !loggingIn.current) navigate('/report', { replace: true });
    }, [isAuthenticated, navigate]);

    if (isAuthenticated && !loggingIn.current) return null;

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!emailAddress || !password) {
            setError('Please enter both your login/mobile and password/pin.');
            return;
        }

        setError('');
        dataReady.current = false;
        loggingIn.current = true;
        runSteps();

        try {
            await authenticateByLogin(emailAddress, password);

            // Read flags directly from JWT — featureFlags from useAuth() won't update until next render
            const token = getToken();
            const flags = parseFlags(token);

            const data = await preloadAllData(flags);
            preloadedRef.current = data;
            dataReady.current = true;
        } catch (ex) {
            clearTimeout(timerRef.current);
            loggingIn.current = false;
            setStepIndex(-1);
            setError(ex.message || 'Connection error. Please try again.');
        }
    };

    const windowStart = Math.max(0, stepIndex - VISIBLE_COUNT + 1);
    const visibleSteps = LOAD_STEPS.slice(windowStart, windowStart + VISIBLE_COUNT);

    return (
        <div className="copia-login-page">
            <img src={opusLogo} alt="OpusApeiro" className="copia-login-page__opus-logo" />

            <div className="copia-login-card">
                <img src={copiaLogo} alt="Copia247" className="copia-login-card__logo" />

                <p className="copia-login-card__instruction">
                    Please enter your login/mobile and password/pin to log in
                </p>

                {error && <div className="copia-login-error">{error}</div>}

                {loading ? (
                    <div className="copia-login-loader">
                        {visibleSteps.map((step, vi) => {
                            const realIdx = windowStart + vi;
                            const done = realIdx < stepIndex;
                            const isFinal = realIdx === LOAD_STEPS.length - 1;
                            return (
                                <div
                                    key={realIdx}
                                    className={`copia-login-loader__step ${done ? 'done' : 'active'} ${isFinal ? 'final' : ''}`}
                                >
                                    <span className="copia-login-loader__icon">
                                        {done ? <BsCheckLg /> : <span className="copia-login-loader__dot" />}
                                    </span>
                                    <span>{step}{!done && !isFinal ? '...' : ''}</span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <form onSubmit={handleLogin}>
                        <div className="copia-login-input-group">
                            <BsPerson className="copia-login-input-group__icon" />
                            <input
                                type="text"
                                placeholder="Email / Mobile"
                                value={emailAddress}
                                onChange={(e) => { setEmailAddress(e.target.value); if (error) setError(''); }}
                                disabled={loading}
                                autoFocus
                            />
                        </div>
                        <div className="copia-login-input-group">
                            <BsKey className="copia-login-input-group__icon" />
                            <input
                                type="password"
                                placeholder="Password / Pin"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                                disabled={loading}
                            />
                        </div>
                        <button
                            type="submit"
                            className="copia-login-btn"
                            disabled={loading || !emailAddress || !password}
                        >
                            Login
                        </button>
                    </form>
                )}

                <button type="button" className="copia-login-forgot" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Forgot password?</button>
            </div>
        </div>
    );
};

export default LoginPage;
